import { api } from '@/lib/api/client';
import type { Json } from '@/features/settings/pageCopyTree';
import type { PreviewToken, SiteMap } from '@/lib/api/types';

/**
 * What the live editor previews: a CMS page by its row id, or a special screen (no page row:
 * the article template, the 404, the newsletter confirmation, maintenance) by its outline key.
 */
export type PreviewTarget = { type: 'page' | 'screen'; id: string };

/**
 * One record's unsaved body, previewed on a page that lists it (a record's editor): the
 * collection's console key, the saved record's id (absent for a new one) and the body the
 * record's own save sends.
 */
export interface RecordDraft {
  collection: string;
  id?: string;
  item: Record<string, unknown>;
}

/** Page outlines + the live-preview draft (documentation/api/admin/pages/list-outlines.md). */
export const pageEditorApi = {
  /** GET /admin/page-outlines — the site map and every page's regions, in on-page order. */
  outlines(): Promise<SiteMap> {
    return api.get<SiteMap>('/admin/page-outlines');
  },
  /** A page or screen preview token for the live editor's frame. */
  mintPreview(target: PreviewTarget, ttlMinutes = 120): Promise<PreviewToken> {
    return api.post<PreviewToken>('/admin/preview-tokens', {
      entity_type: target.type,
      entity_id: target.id,
      ttl_minutes: ttlMinutes,
    });
  },
  /**
   * PUT /admin/preview-tokens/{token}/draft — the unsaved working documents the preview
   * renders, and the one record being edited beside its page. Each call replaces the whole
   * draft: an empty map clears the copy, no record clears the record.
   */
  putDraft(
    token: string,
    copy: Record<string, Json>,
    record?: RecordDraft | null,
  ): Promise<{ namespaces: string[] }> {
    return api.put<{ namespaces: string[] }>(
      `/admin/preview-tokens/${encodeURIComponent(token)}/draft`,
      record ? { copy, record } : { copy },
    );
  },
};
